/**
 * AI 가 정답으로 지목한 슬롯을 실제로 찍어도 되는지 판정한다.
 *
 * 빈 객관식 카드는 0번이 `isAnswer: true` 로 시작한다(getBlankQuizItem). 그건 교사의
 * 선택이 아니라 기본값이므로, "정답이 이미 있다" 로 보면 AI 가 다른 칸을 정답으로
 * 골라도 표시는 0번에 남아 **내용과 정답이 어긋난 문항**이 된다.
 *
 * 판정 기준은 "정답으로 찍힌 선지에 내용이 있는가" 다:
 * - 내용 있는 정답이 하나라도 있으면 교사의 선택 → 건드리지 않는다.
 * - 전부 빈 칸에 찍혀 있으면 기본값 → AI 의 지목으로 옮긴다(옛 표시는 지운다).
 */
import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';

import {
  isSlotFilled,
  slotSchemaOf,
  slotsOf,
} from './questionCardAiSlotSchema';

export type AnswerSlotDecision = { mark: number[]; clear: number[] };

/** 내용 있는 정답이 하나라도 있으면 교사의 선택이다(빈 칸 정답 표시는 기본값). */
export const hasTeacherChosenAnswer = (quiz: BuildQuizListItem) =>
  slotsOf(quiz).some(slot => slot.isAnswer && isSlotFilled(slot));

/**
 * 서버가 정답 자리를 직접 만들어 내려주는 요청인가(후보 모드). 교사의 정답에 내용이 없거나,
 * 내용 있는 정답이 있어도 정답으로 찍힌 빈 칸이 남아 있으면 그 빈 칸은 서버가 정답으로 채운다.
 * 서버의 모드 판정(derive_slot_plan)과 같은 정의다.
 */
export const needsServerAnswerPick = (quiz: BuildQuizListItem) =>
  !hasTeacherChosenAnswer(quiz) ||
  slotsOf(quiz).some(slot => slot.isAnswer && !isSlotFilled(slot));

export const resolveAnswerSlotToMark = (
  quiz: BuildQuizListItem,
  answerSlotIds: string[] | null | undefined,
  filled: { slotId: string }[],
): AnswerSlotDecision | null => {
  if (!slotSchemaOf(quiz.type)?.acceptsAnswerPick) return null;
  if (!answerSlotIds || answerSlotIds.length === 0) return null;

  const slots = slotsOf(quiz);
  const designated = slots
    .map((slot, index) => ({ slot, index }))
    .filter(({ slot }) => slot.isAnswer);
  if (hasTeacherChosenAnswer(quiz)) return null;

  // 이번에 우리가 채운 슬롯만 정답으로 찍는다(사용자가 직접 채운 칸 등은 제외).
  const filledIds = new Set(filled.map(write => write.slotId));
  const mark = answerSlotIds
    .filter(slotId => filledIds.has(slotId))
    .map(slotId => slots.findIndex(slot => slot.key === slotId))
    .filter(index => index !== -1);
  if (mark.length === 0) return null;

  return {
    mark,
    clear: designated
      .map(({ index }) => index)
      .filter(index => !mark.includes(index)),
  };
};
